import InputContainer from "./InputContainer";
import InputOptions from "./InputOptions";
import { twMerge } from "tailwind-merge";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Constants } from "../utils";
import { SVGs } from "../assets";

export type PhoneNumberFieldClassNames = {
  root?: string;
  label?: string;
  container?: string;
  error?: string;
  flag?: string;
  divider?: string;
  input?: string;
  menu?: string;
  search?: string;
};

export type PhoneNumberFieldProps = {
  label?: string;
  errorMessage?: string;
  classNames?: PhoneNumberFieldClassNames;
} & Omit<React.ComponentPropsWithoutRef<"input">, "type">;

const phoneCountryOptions = [...Constants.countries]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(({ dial, name, iso2 }) => ({
    value: dial,
    label: `${name} ${dial}`,
    icon: (
      <img
        src={Constants.flagSrc(iso2)}
        alt=""
        aria-hidden="true"
        className="h-[14px] w-[20px] object-cover"
      />
    ),
  }));

function findCountry(value: string | undefined): Constants.Country | undefined {
  const v = (value ?? "").replace(/\s+/g, "");
  if (!v.startsWith("+")) return undefined;
  let best: Constants.Country | undefined;
  for (const country of Constants.countries) {
    if (!v.startsWith(country.dial)) continue;
    if (!best || country.dial.length > best.dial.length) best = country;
  }
  return best;
}

function getDefaultCountry(): Constants.Country {
  const { countries, defaultPhoneDial } = Constants;
  return (
    countries.find(({ dial }) => dial === defaultPhoneDial) ?? countries[0]!
  );
}

function formatWith(
  country: Constants.Country,
  nationalDigits: string,
): string {
  if (!country.nationalFormat) return nationalDigits;
  return nationalDigits.replace(
    country.nationalFormat.re,
    country.nationalFormat.fmt,
  );
}

function nationalDigitsFor(
  value: string,
  country: Constants.Country,
): string {
  const digits = value.replace(/\D/g, "");
  const dialDigits = country.dial.replace(/\D/g, "");
  return digits.startsWith(dialDigits)
    ? digits.slice(dialDigits.length)
    : digits;
}

function sanitizeAndFormatPhone(raw: string): string {
  const digits = raw.trim().replace(/\D/g, "");
  if (!digits) return "";

  const all = `+${digits}`;
  const country = findCountry(all);
  if (!country) return all;

  return `${country.dial}${formatWith(country, nationalDigitsFor(all, country))}`;
}

function applyCountry(current: string, country: Constants.Country): string {
  const previous = findCountry(current) ?? getDefaultCountry();
  return `${country.dial}${formatWith(country, nationalDigitsFor(current, previous))}`;
}

function emitPhoneChange(
  el: HTMLInputElement,
  next: string,
  onChange?: React.ComponentPropsWithoutRef<"input">["onChange"],
) {
  el.value = next;
  onChange?.({
    target: el,
    currentTarget: el,
  } as React.ChangeEvent<HTMLInputElement>);
}

export default function PhoneNumberField({
  id,
  label,
  errorMessage,
  classNames,
  required,
  className,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  value,
  onChange,
  onPaste,
  onKeyDown: _onKeyDown,
  inputMode,
  pattern,
  disabled,
  ...inputProps
}: PhoneNumberFieldProps) {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const normalizedValue =
    typeof value === "string" ? sanitizeAndFormatPhone(value) : "";
  const { dial, iso2 } = findCountry(normalizedValue) ?? getDefaultCountry();

  return (
    <InputContainer
      id={id}
      label={label}
      required={required}
      errorMessage={errorMessage}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      classNames={{
        ...classNames,
        container: twMerge("gap-0", classNames?.container),
      }}
      className={className}
    >
      <InputOptions
        disabled={disabled}
        searchable
        searchPlaceholder={t("general.search")}
        value={dial}
        options={phoneCountryOptions}
        className="h-full shrink-0"
        classNames={{
          menu: twMerge("min-w-[300px]", classNames?.menu),
          search: classNames?.search,
          option: "items-center",
          trigger: "h-full",
        }}
        onChange={(nextDial) => {
          const country = Constants.countries.find((c) => c.dial === nextDial);
          if (!country || !inputRef.current) return;
          emitPhoneChange(
            inputRef.current,
            applyCountry(normalizedValue, country),
            onChange,
          );
        }}
      >
        <button
          type="button"
          disabled={disabled}
          aria-label={t("general.countryCode")}
          aria-haspopup="menu"
          className={twMerge(
            "box-border h-full flex shrink-0 py-0 px-[.5rem] items-center gap-[5px] border-r border-input-border",
            classNames?.flag,
          )}
        >
          <img
            src={Constants.flagSrc(iso2)}
            alt=""
            aria-hidden="true"
            className="h-[14px] w-[20px] shrink-0 object-cover"
          />
          <SVGs.ChevronDown
            aria-hidden="true"
            className="h-[10px] w-[10px] shrink-0 text-text-secondary"
          />
        </button>
      </InputOptions>

      <InputContainer.Input
        {...inputProps}
        ref={inputRef}
        type="tel"
        required={required}
        disabled={disabled}
        value={normalizedValue}
        inputMode={inputMode ?? "tel"}
        pattern={pattern ?? "[0-9+\\s()-]*"}
        className={twMerge(classNames?.input)}
        onPaste={(e) => {
          onPaste?.(e);
          if (e.defaultPrevented) return;
          e.preventDefault();
          emitPhoneChange(
            e.currentTarget,
            sanitizeAndFormatPhone(
              normalizedValue.slice(
                0,
                e.currentTarget.selectionStart ?? normalizedValue.length,
              ) +
                e.clipboardData.getData("text") +
                normalizedValue.slice(
                  e.currentTarget.selectionEnd ?? normalizedValue.length,
                ),
            ),
            onChange,
          );
        }}
        onChange={(e) => {
          const next = sanitizeAndFormatPhone(e.currentTarget.value);
          if (next !== e.currentTarget.value) e.currentTarget.value = next;
          onChange?.(e);
        }}
      />
    </InputContainer>
  );
}
